import Vue from "vue";
import Component from "vue-class-component";

@Component
export default class FecMixin extends Vue {
  // Verrifie si les libelles de compte sont identiques
  public isToAgregate(dataPreconfig: any): boolean {
    const hasSimilarComptes = (comptes: string[]): boolean => {
      const seen = new Set<string>();
      for (const compteRaw of comptes) {
        const compte = compteRaw.toLowerCase();
        if (seen.has(compte)) {
          return true;
        }
        seen.add(compte);
      }
      return false;
    };

    const comptesDebits: string[] = [];
    if (
      dataPreconfig &&
      dataPreconfig.debits &&
      dataPreconfig.debits.famille_reglement &&
      Array.isArray(dataPreconfig.debits.famille_reglement)
    ) {
      for (const item of dataPreconfig.debits.famille_reglement) {
        if (item && item.compte !== undefined) {
          comptesDebits.push(String(item.compte));
        }
      }
    }
    if (hasSimilarComptes(comptesDebits)) {
      return true;
    }

    const comptesNormal: string[] = [];
    if (
      dataPreconfig &&
      dataPreconfig.credits &&
      dataPreconfig.credits.regroupement &&
      dataPreconfig.credits.regroupement.NORMAL
    ) {
      const normal = dataPreconfig.credits.regroupement.NORMAL;
      for (const taux in normal) {
        if (Object.prototype.hasOwnProperty.call(normal, taux)) {
          const group = normal[taux];
          if (group && group.HT && group.HT.compte !== undefined) {
            comptesNormal.push(String(group.HT.compte));
          }
          if (group && group.TVA && group.TVA.compte !== undefined) {
            comptesNormal.push(String(group.TVA.compte));
          }
        }
      }
    }
    if (hasSimilarComptes(comptesNormal)) {
      return true;
    }

    const comptesLoc: string[] = [];
    if (
      dataPreconfig &&
      dataPreconfig.credits &&
      dataPreconfig.credits.regroupement &&
      dataPreconfig.credits.regroupement.PAR_LOC
    ) {
      const parLoc = dataPreconfig.credits.regroupement.PAR_LOC;
      for (const loc in parLoc) {
        if (Object.prototype.hasOwnProperty.call(parLoc, loc)) {
          const tauxList = parLoc[loc];
          for (const taux in tauxList) {
            if (Object.prototype.hasOwnProperty.call(tauxList, taux)) {
              const compte = tauxList[taux] && tauxList[taux].compte;
              if (compte !== undefined) {
                comptesLoc.push(String(compte));
              }
            }
          }
        }
      }
    }
    if (hasSimilarComptes(comptesLoc)) {
      return true;
    }

    return false;
  }

  /**
   * Valide tous les champs "compte" dans la structure JSON
   * @param dataPreconfig - Les données de préconfiguration FEC
   * @returns object - Résultat de la validation avec détails des erreurs
   */
  public validateComptesFormat(dataPreconfig: any): {
    isValid: boolean;
    errors: string[];
    invalidComptes: Array<{ path: string; value: string | number }>;
  } {
    const errors: string[] = [];
    const invalidComptes: Array<{ path: string; value: string | number }> = [];

    if (
      dataPreconfig &&
      dataPreconfig.debits &&
      dataPreconfig.debits.famille_reglement &&
      Array.isArray(dataPreconfig.debits.famille_reglement)
    ) {
      dataPreconfig.debits.famille_reglement.forEach(
        (item: any, index: number) => {
          if (item && item.compte !== undefined) {
            if (!this.isCompteFormatValid(item.compte)) {
              const path = `debits.famille_reglement[${index}].compte`;
              errors.push(
                `Compte débit invalide: ${item.compte} (${
                  item.lib_famille || "N/A"
                })`
              );
              invalidComptes.push({ path, value: item.compte });
            }
          }
        }
      );
    }

    if (
      dataPreconfig &&
      dataPreconfig.comptage &&
      dataPreconfig.comptage.compte !== undefined
    ) {
      if (!this.isCompteFormatValid(dataPreconfig.comptage.compte)) {
        const path = "comptage.compte";
        errors.push(
          `Compte comptage invalide: ${dataPreconfig.comptage.compte}`
        );
        invalidComptes.push({ path, value: dataPreconfig.comptage.compte });
      }
    }

    if (
      dataPreconfig &&
      dataPreconfig.credits &&
      dataPreconfig.credits.regroupement &&
      dataPreconfig.credits.regroupement.NORMAL
    ) {
      const normal = dataPreconfig.credits.regroupement.NORMAL;
      for (const taux in normal) {
        if (Object.prototype.hasOwnProperty.call(normal, taux)) {
          const group = normal[taux];

          if (group && group.HT && group.HT.compte !== undefined) {
            if (!this.isCompteFormatValid(group.HT.compte)) {
              const path = `credits.regroupement.NORMAL.${taux}.HT.compte`;
              errors.push(
                `Compte HT NORMAL invalide (${taux}%): ${group.HT.compte}`
              );
              invalidComptes.push({ path, value: group.HT.compte });
            }
          }

          if (group && group.TVA && group.TVA.compte !== undefined) {
            if (!this.isCompteFormatValid(group.TVA.compte)) {
              const path = `credits.regroupement.NORMAL.${taux}.TVA.compte`;
              errors.push(
                `Compte TVA NORMAL invalide (${taux}%): ${group.TVA.compte}`
              );
              invalidComptes.push({ path, value: group.TVA.compte });
            }
          }
        }
      }
    }

    if (
      dataPreconfig &&
      dataPreconfig.credits &&
      dataPreconfig.credits.regroupement &&
      dataPreconfig.credits.regroupement.PAR_LOCALISATION
    ) {
      const parLoc = dataPreconfig.credits.regroupement.PAR_LOCALISATION;
      for (const loc in parLoc) {
        if (Object.prototype.hasOwnProperty.call(parLoc, loc)) {
          const tauxList = parLoc[loc];
          for (const taux in tauxList) {
            if (Object.prototype.hasOwnProperty.call(tauxList, taux)) {
              const compte = tauxList[taux] && tauxList[taux].compte;
              if (compte !== undefined) {
                if (!this.isCompteFormatValid(compte)) {
                  const path = `credits.regroupement.PAR_LOCALISATION.${loc}.${taux}.compte`;
                  errors.push(
                    `Compte PAR_LOCALISATION invalide (${loc}, ${taux}%): ${compte}`
                  );
                  invalidComptes.push({ path, value: compte });
                }
              }
            }
          }
        }
      }
    }

    if (
      dataPreconfig &&
      dataPreconfig.credits &&
      dataPreconfig.credits.regroupement &&
      dataPreconfig.credits.regroupement.SOLIDE_LIQUIDE
    ) {
      const solideLiquide = dataPreconfig.credits.regroupement.SOLIDE_LIQUIDE;
      for (const type in solideLiquide) {
        if (Object.prototype.hasOwnProperty.call(solideLiquide, type)) {
          const tauxList = solideLiquide[type];
          for (const taux in tauxList) {
            if (Object.prototype.hasOwnProperty.call(tauxList, taux)) {
              const compte = tauxList[taux] && tauxList[taux].compte;
              if (compte !== undefined) {
                if (!this.isCompteFormatValid(compte)) {
                  const path = `credits.regroupement.SOLIDE_LIQUIDE.${type}.${taux}.compte`;
                  errors.push(
                    `Compte SOLIDE_LIQUIDE invalide (${type}, ${taux}%): ${compte}`
                  );
                  invalidComptes.push({ path, value: compte });
                }
              }
            }
          }
        }
      }
    }

    if (
      dataPreconfig &&
      dataPreconfig.credits &&
      dataPreconfig.credits.regroupement &&
      dataPreconfig.credits.regroupement.SOLIDE_LIQUIDE_PROFIT
    ) {
      const solideLiquideProfit =
        dataPreconfig.credits.regroupement.SOLIDE_LIQUIDE_PROFIT;
      for (const localisation in solideLiquideProfit) {
        if (
          Object.prototype.hasOwnProperty.call(
            solideLiquideProfit,
            localisation
          )
        ) {
          const locData = solideLiquideProfit[localisation];
          for (const type in locData) {
            if (Object.prototype.hasOwnProperty.call(locData, type)) {
              const tauxList = locData[type];
              for (const taux in tauxList) {
                if (Object.prototype.hasOwnProperty.call(tauxList, taux)) {
                  const compte = tauxList[taux] && tauxList[taux].compte;
                  if (compte !== undefined) {
                    if (!this.isCompteFormatValid(compte)) {
                      const path = `credits.regroupement.SOLIDE_LIQUIDE_PROFIT.${localisation}.${type}.${taux}.compte`;
                      errors.push(
                        `Compte SOLIDE_LIQUIDE_PROFIT invalide (${localisation}, ${type}, ${taux}%): ${compte}`
                      );
                      invalidComptes.push({ path, value: compte });
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      invalidComptes,
    };
  }

  /**
   * Validation globale incluant format des comptes et doublons
   * @param dataPreconfig - Les données de préconfiguration FEC
   * @returns boolean - True si toutes les validations passent
   */
  public isValidPreconfig(dataPreconfig: any, isAggregateLines: boolean): {
    isValid: boolean;
    errors: string[];
  } {
    const allErrors: string[] = [];

    const formatValidation = this.validateComptesFormat(dataPreconfig);
    if (!formatValidation.isValid) {
      allErrors.push(...formatValidation.errors);
    }
    const isAggregationEnabled = isAggregateLines === true;
    if (!isAggregationEnabled) {
      const hasAggregation = this.isToAgregate(dataPreconfig);
      if (hasAggregation) {
          allErrors.push("Des comptes en doublon ont été détectés - activez l'agrégation ou corrigez les doublons");
      }
    }

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
    };
  }

  /**
   * Vérifie si un numéro de compte commence par 3 chiffres
   * @param compte - Le numéro de compte à vérifier
   * @returns boolean - True si valide, false sinon
   */
  private isCompteFormatValid(compte: string | number): boolean {
    const compteStr = String(compte);
    const regex = /^\d{3}/;
    return regex.test(compteStr);
  }
}
